/**
 * Logging levels in winston conform to the severity ordering specified by RFC5424
 * { 
  emerg: 0, 
  alert: 1, 
  crit: 2, 
  error: 3, 
  warning: 4, 
  notice: 5, 
  info: 6, 
  debug: 7
}
 */

//declare permette di "aggiungere" al dizionario typescript delle keyword in modo da permettere la compilazione  
//anche quando non presenti nell'environment
declare var require: any; //non presente nei progetti web. In compilazione si ha l'errore "Cannot find name 'require'."
declare var global: any;  //non presente nei progetti web. In compilazione si ha l'errore "Cannot find name 'global'."
declare var window: any;  //non presente nei progetti server. In compilazione si ha l'errore "Cannot find name 'window'."

import { buildPath } from './util';
import { Tracking } from './tracking';
import { isDesktopApp } from './environment-util';

let tempLog = "";

let appContext: any;


/**
 * Scenari di uso: 
 * - in WEB APP viene instanziato nel contesto di ANGULAR e logga solo su console
 * - in DESKTOP APP ne vengono create almeno due istanze: 
 *  - ogni renderer process ne crea una istanza nel proprio contesto e logga solo su console 
 *  - nel main process ne viene creata una istanza provvisioria che poi viene sostituita a seguito dell'init (quando sono disponibili 
 *    le variabili di environment.json necessarie per l'inizializzazione definitiva) effettuato in electronCommon
 * - in SERVER APP ne viene creata una istanza provvisioria che poi viene sostituita a seguito dell'init (quando sono disponibili 
 *   le variabili di environment.json necessarie per l'inizializzazione definitiva) effettuato in nodeCommon
 * 
 * Viene inizializzata con: 
 * - per app di tipo WEB o rendererProcess di app di tipo DESKTOP -> un classe contenente i metodi error,info e debug che scrivono su console
 * - altrimenti -> un logger temporaneo che scrive su console e su una variabile temporanea che verrà riversata sul file di log definitivo
 *                 al termine della sua inizializzazione
 * 
 * Viene richiamato:
 * - da mainProcess e SERVER tramite modulo Tracking
 * - da rendererProcess e WEB APP direttamente  
 */
export let logger = (() => {
    
    let isBrowserProcess = false; //app di tipo WEB o rendererProcess di app di tipo DESKTOP

    try {
        window; //Se window non è definito si ottiene "ReferenceError: window is not defined"
        isBrowserProcess = true;
    } catch (error) {}

    if(isBrowserProcess) 
        return new (class Logger{
            public error = (message) => {
                if(isDesktopApp())
                    window['require']('electron').ipcRenderer.send('LOG', 'ERROR', message);
        
                 console.log(message);
            }

            public info = (message) => {
                const level = appContext.environment.getValue("uiLogging.level");
                if (level && ['INFO','DEBUG'].includes(level)){
                    if(isDesktopApp())
                        window['require']('electron').ipcRenderer.send('LOG', 'INFO', message);
                    
                    console.log(message);
                }
            }
            public debug = (message) => {
                const level = appContext.environment.getValue("uiLogging.level");
                if (level && level == 'DEBUG'){
                    if(isDesktopApp())
                        window['require']('electron').ipcRenderer.send('LOG', 'DEBUG', message);
                    
                    console.log(message);
                }
            }
        })
    else {
        let os;
        let winston;

        try { //questo try-catch serve solo per consentire la compilazione visto che contiene require dichiarati con un declare
            os = require('os');
            winston = require('winston');
        } catch (error) {}

        return winston.createLogger({
            level: 'debug',
            format: winston.format.combine(
                winston.format.splat(),
                winston.format.simple(),
                winston.format.timestamp({
                    format: 'YYYY-MM-DD HH:mm:ss'
                }),
                winston.format.printf(info => {
                    tempLog+=os.EOL+info.message; //variabile temporanea che verrà riversata sul file di log definitivo al termine della sua inizializzazione
                    return `${info.message}`})
            ),
            transports: new winston.transports.Console(),
        });
    }
    })(); //invocazione diretta della funzione appena definita

/**
 * Esegue l'inizializzazione di winston, rivalorizza la variabile logger e viene richiamato solo dai processi node (server e electron main)
 * Viene richiamato quando sono disponibili le variabili di environment.json necessarie per l'inizializzazione definitiva
 */
export const initLogger = () => {
    let trackingContext;

    try {

        appContext = global['appContext']
        trackingContext = Tracking.start();
        
        require('fs').existsSync(buildPath(appContext.environment.getValue('logging.folderPath'))) || require('fs').mkdirSync(buildPath(appContext.environment.getValue('logging.folderPath')))

        const transports = [
            new (require("winston-daily-rotate-file"))({
                filename: buildPath(appContext.environment.getValue('logging.folderPath'), "error.log"),
                level: 'error',
                timestamp: () => (new Date().toLocaleTimeString()),
                datePattern: 'YYYY-MM-DD',
                prepend: true,
                maxsize: 10000000, // en octet,
                maxfile: 10,
                colorize: false,
                json: false,
                prettyPrint: true
            }),
            new (require("winston-daily-rotate-file"))({
                filename: buildPath(appContext.environment.getValue('logging.folderPath'), "combined.log"),
                timestamp: () => (new Date().toLocaleTimeString()),
                datePattern: 'YYYY-MM-DD',
                prepend: true,
                maxsize: 10000000, // en octet,
                maxfile: 10,
                colorize: false,
                json: false,
                prettyPrint: true
            })
        ]

        if (appContext.environment.getValue('logging.logToConsole') == true)
            transports.push(new (require('winston')).transports.Console());

        logger = require('winston').createLogger({
            level: appContext.environment.getValue('logging.level')?appContext.environment.getValue('logging.level').toLowerCase():'ERROR',
            format: require('winston').format.combine(
                require('winston').format.splat(),
                require('winston').format.simple(),
                require('winston').format.timestamp({
                    format: 'YYYY-MM-DD HH:mm:ss'
                }),
                require('winston').format.printf(info => `${info.message}`)
            ),
            transports: transports,
        });

        logger.info(tempLog);
        tempLog = null;

        Tracking.end(trackingContext);

    } catch (error) {
        Tracking.endWithError(trackingContext,error);
        throw(error);
    }
}


/**
 * Logger utilizzato dal main process per loggare informazioni ricevute dal renderer process tramite ipc event
 */
export let rendererProcessLogger;

/**
 * Esegue l'inizializzazione di winston, valorizza la variabile rendererProcessLogger e viene richiamato solo dal main process electron 
 * Viene richiamato quando sono disponibili le variabili di environment.json necessarie per l'inizializzazione definitiva
 */
export const initRendererProcessLogger = () => {
    let trackingContext;

    try {

        appContext = global['appContext']
        trackingContext = Tracking.start();
        
        require('fs').existsSync(buildPath(appContext.environment.getValue('uiLogging.folderPath'))) || require('fs').mkdirSync(buildPath(appContext.environment.getValue('uiLogging.folderPath')))

        const transports = [
            new (require("winston-daily-rotate-file"))({
                filename: buildPath(appContext.environment.getValue('uiLogging.folderPath'), "renderer"),
                extension: '.log',
                timestamp: () => (new Date().toLocaleTimeString()),
                datePattern: 'YYYY-MM-DD',
                prepend: true,
                maxsize: 10000000, // en octet,
                maxfile: 10,
                colorize: false,
                json: false,
                prettyPrint: true
            })
        ]

        rendererProcessLogger = require('winston').createLogger({
            level: appContext.environment.getValue('uiLogging.level')?appContext.environment.getValue('uiLogging.level').toLowerCase():'ERROR',
            format: require('winston').format.combine(
                require('winston').format.splat(),
                require('winston').format.simple(),
                require('winston').format.timestamp({
                    format: 'HH:mm:ss.SSS'
                }),
                require('winston').format.printf(info => `${info.timestamp};${info.message.split(require('os').EOL).join("___")}`)
            ),
            transports: transports,
        });

        Tracking.end(trackingContext);

    } catch (error) {
        Tracking.endWithError(trackingContext,error);
        throw(error);
    }
}


/**
 * Logger utilizzato dalle server app per fare audit delle operazioni eseguite
 */
export let auditLogger;

//TODO: implementare per audit lato server
/**
 * Esegue l'inizializzazione di winston, valorizza la variabile auditLogger e viene richiamato solo dai processi server 
 * Viene richiamato quando sono disponibili le variabili di environment.json necessarie per l'inizializzazione definitiva
 */
export const initAuditLogger = () => {}

